// src/pages/Advertisement/AdOverview.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChoshiiLogo from '../ChoshiiLogo';
import { useAuth } from '../../shared/contexts/AuthContext';
import { useMediaPreview } from '../../shared/contexts/MediaPreviewContext';
import { getOverlayForFilter, type FilterPresetId } from '../../shared/components/filterHelpers.web';
import api from '../../shared/api/axios';
import { ensureJpeg } from '../../shared/utils/ensureJpeg';
import uploadViaBackendHls from '../../shared/utils/uploadViaBackendHls';
import { ensureMp4H264Aac } from '../../shared/utils/videoPipeline';
import CalendarModal from "./CalendarModal";
import CountriesDropdown, { CONTINENT_COUNTRIES }  from "./CountryChoice";
import CityChoice from "./CityChoiceAd";
import type { MediaItem } from '../../shared/contexts/MediaPreviewContext';
import type { CitySelection } from "./CityChoiceAd";


type TargetingMode =  'continent' | 'countries' | 'cities';
type DeliveryMode = 'balanced' | 'frequent' | 'light';


interface LocationState {
  targetUrl?: string;
  buttonLabel?: string;
  fromPath?: string;
}


const CONTINENTS = Object.keys(CONTINENT_COUNTRIES);

const ALL_COUNTRIES = Object.values(CONTINENT_COUNTRIES)
  .flat()
  // dedupe by ISO code
  .filter((c, i, arr) => arr.findIndex((x) => x.code === c.code) === i)
  // optional: keep dropdown alphabetic even before typing
  .sort((a, b) => a.name.localeCompare(b.name));




// --- match media layout from AdUpload ---
const MEDIA_LEFT_NUDGE = 0;
const MEDIA_GAP = 40;
const MEDIA_TILE_WIDTH = 610;
const CONTENT_X_OFFSET = 40;

const toISODate = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// basic overlay builder used in upload page as well
const overlayStyleFor = (fid?: string | null): React.CSSProperties | null => {
  const id = (fid as FilterPresetId) || 'none';
  const { tint, opacity } = getOverlayForFilter(id);
  return tint
    ? {
        position: 'absolute',
        inset: 0,
        backgroundColor: tint,
        opacity,
        pointerEvents: 'none',
        borderRadius: 12,
      }
    : null;
};

const AdOverview: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: LocationState };
  const { t } = useTranslation();
  const { user } = useAuth();
  const { mediaItems } = useMediaPreview();

  const [objectURLs, setObjectURLs] = useState<Record<string, string>>({});
  const [isLaunching, setIsLaunching] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeField, setActiveField] = useState<"start" | "end">("start");
  const startInputRef = React.useRef<HTMLInputElement | null>(null);
  const endInputRef = React.useRef<HTMLInputElement | null>(null);
  const activeAnchorRef = activeField === "start" ? startInputRef : endInputRef;

  // --- incoming data from AdUpload ---

  const targetUrl = state?.targetUrl || '';
  const buttonLabel = state?.buttonLabel || '';
  const [campaignName, setCampaignName] = useState<string>(
    () => buttonLabel || 'My first ad'
  );
//For Prepare Upload
  async function prepareFileForUpload(file: File): Promise<File> {
    if (file.type.startsWith('image/')) {
      return await ensureJpeg(file, 0.85);
    }
  
    if (file.type.startsWith('video/')) {
      try {
        const out = await ensureMp4H264Aac(file);
        if (out.type === 'video/mp4' && !/\.mp4$/i.test(out.name)) {
          const base = out.name.replace(/\.[^.]+$/, '');
          return new File([out], `${base}.mp4`, { type: 'video/mp4' });
        }
        return out;
      } catch (e) {
        console.warn('[AdOverview] ffmpeg wasm unavailable, skipping client transcode:', e);
        const name = /\.mp4$/i.test(file.name)
          ? file.name
          : file.name.replace(/\.[^.]+$/, '') + '.mp4';
        return new File([file], name, { type: file.type || 'video/mp4' });
      }
    }
  
    return file;
  }
  const formatISO = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : "");

  const dateInputStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: "pointer",
  };


  useEffect(() => {
  if (mediaItems.length === 0) {
    alert("Your selected media is no longer available. Please reselect it.");
    navigate("/advertisement/new", { replace: true });
  }
}, [mediaItems, navigate]);


  // build object URLs for file-based previews
 useEffect(() => {
  const toCreate: Record<string, string> = {};

  for (const item of mediaItems) {
    if (!item.file) continue; // previews only for files
    if (objectURLs[item.id]) continue;

    toCreate[item.id] = URL.createObjectURL(item.file);
  }

  if (Object.keys(toCreate).length) {
    setObjectURLs(prev => ({ ...prev, ...toCreate }));
  }

  return () => {
    Object.values(objectURLs).forEach(URL.revokeObjectURL);
  };
}, [mediaItems]);



  const srcFor = (item: MediaItem): string | undefined => {
    if (objectURLs[item.id]) return objectURLs[item.id];
    if (item.url && /^https?:\/\//i.test(item.url)) return item.url;
    return undefined;
  };
  const todayISODate = () => new Date().toISOString().slice(0, 10);

  const isVideo = (item: MediaItem): boolean => {
    if (item.file) return item.file.type.startsWith('video/');
    if (item.url) return /\.(mp4|webm|mov|m4v)$/i.test(item.url);
    return false;
  };

  // ------------- Targeting + Budget state -------------
  const [targetingMode, setTargetingMode] = useState<TargetingMode>('continent');
  const [selectedContinent, setSelectedContinent] = useState<string>('Europe');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [dailyBudget, setDailyBudget] = useState<number>(5);
  const [totalBudget, setTotalBudget] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('balanced');

  const [selectedCityCountryISO, setSelectedCityCountryISO] = useState<string>("");
  const [selectedCityState, setSelectedCityState] = useState<string>("");
  const [selectedCities, setSelectedCities] = useState<CitySelection[]>([]);

  const COUNTRY_CODE_TO_NAME = useMemo(() => {
    return new Map(ALL_COUNTRIES.map((c) => [c.code, c.name]));
  }, []);
  
  const toggleCity = (countryISO: string, city: string, state?: string) => {
    setSelectedCities((prev) => {
      const exists = prev.some(
        (x) => x.countryISO === countryISO && x.city === city && (x.state ?? "") === (state ?? "")
      );
  
      if (exists) {
        return prev.filter(
          (x) => !(x.countryISO === countryISO && x.city === city && (x.state ?? "") === (state ?? ""))
        );
      }
  
      return [...prev, { countryISO, city, state }];
    });
  };
  

  // simple impression estimate
  const impressionEstimate = useMemo(() => {
    const base = Math.max(dailyBudget, 0);
    const min = Math.round(base * 80);
    const max = Math.round(base * 160);
    return { min, max };
  }, [dailyBudget]);

  const toggleCountry = (country: string) => {
    setSelectedCountries((prev) =>
      prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
    );
  };

const launchAd = async () => {
    const uploadedMedia: any[] = [];

    const looksGenericCameraName = (base: string) => {
      const b = base.toLowerCase();
      return (
        b === 'media-0' || b === 'media-1' || b === 'media' ||
        b === 'video-0' || b === 'video' ||
        /^img[_-]?\d+$/i.test(base) ||
        /^vid[_-]?\d+$/i.test(base) ||
        /^pxl[_-]?\d+/i.test(base) ||
        base.length < 4
      );
    };

    const typePrefix = (f: File) =>
      f.type.startsWith('video/')
        ? 'video'
        : f.type.startsWith('image/')
        ? 'image'
        : 'file';

    const normalizeBaseFromFile = (f: File) => {
      const rawBase = (f.name || '').replace(/\.[^.]+$/, '');
      const safeRaw = rawBase.replace(/[^A-Za-z0-9._-]/g, '');
      if (!safeRaw || looksGenericCameraName(safeRaw)) {
        const stamp = new Date().toISOString().replace(/[:.]/g, '');
        return `${typePrefix(f)}_${stamp}`;
      }
      return safeRaw;
    };

    const extOf = (f: File) =>
      (f.name.match(/\.[a-z0-9]+$/i)?.[0]?.toLowerCase()) ||
      (f.type.startsWith('video/') ? '.mp4'
        : f.type.startsWith('image/') ? '.jpg'
        : '.bin');

    for (let i = 0; i < mediaItems.length; i++) {
      const item = mediaItems[i];

     if (!item.file) {
  throw new Error("Missing media file");
}

      const prepared = await prepareFileForUpload(item.file);

      if (prepared.type.startsWith("video/")) {
        const ext = extOf(prepared);
        const base = normalizeBaseFromFile(prepared);
        const stamp = new Date().toISOString().replace(/[:.]/g, "");
        const uniqueName = `${base}_${stamp}${ext}`;

        const fileWithUniqueName = new File([prepared], uniqueName, {
          type: prepared.type || "application/octet-stream",
        });

        const upload = await uploadViaBackendHls(fileWithUniqueName);

        if (!upload.videoId || !upload.hlsUrl) {
  throw new Error("Video upload did not return a completed HLS result");
}

        uploadedMedia.push({
          videoId: upload.videoId,
          isHls: true,
          hlsUrl: upload.hlsUrl ?? null,
          dashUrl: upload.dashUrl ?? null,
          posterUrl: upload.posterUrl ?? null,
          type: "video",
          sizeBytes: prepared.size ?? null,
          filterId: item.filterId ?? null,
          musicTitle: item.musicTitle ?? null,
          musicArtist: item.musicArtist ?? null,
        });
      } else {
        const stamp = new Date().toISOString().replace(/[:.]/g, "");
        const uniqueName = `${Date.now()}-image_${stamp}_${stamp}.jpeg`;

        const fileWithUniqueName = new File([prepared], uniqueName, {
          type: "image/jpeg",
        });

        const form = new FormData();
        form.append("file", fileWithUniqueName);

        const res = await api.post("/mediaImg/upload-image", form);

if (!res.data?.url) {
  throw new Error("Image upload did not return a URL");
}

        uploadedMedia.push({
          url: res.data.url,
          type: "image",
          sizeBytes: prepared.size ?? null,
          mime: "image/jpeg",
          isHls: false,
          filterId: item.filterId ?? null,
          musicTitle: item.musicTitle ?? null,
          musicArtist: item.musicArtist ?? null,
        });
      }
    }

    if (!uploadedMedia.length) {
  throw new Error("No media was uploaded");
}

    const mediaRes = await api.post("/media/ad", {
      media: uploadedMedia,
      location: { city: "Ad", country: "Ad", coords: { lat: 1, lng: 1 } },
      hashtags: [],
    });

    const mediaId = mediaRes.data.media?._id || mediaRes.data._id;

    if (!mediaId) {
  throw new Error("The media route did not return a media ID");
}

    await api.post("/ads", {
      mediaId,
      name: campaignName || "Untitled campaign",
      clickUrl: targetUrl.trim(),
      ctaText: buttonLabel || "Learn more",
      dailyBudget,
      totalBudget: totalBudget === "" ? undefined : totalBudget,
      targeting: {
        continents: targetingMode === "continent" ? [selectedContinent] : [],
        countries: targetingMode === "countries" ? selectedCountries : [],
        cities:
          targetingMode === "cities"
            ? selectedCities.map(
                (x) => `${x.countryISO}|${x.state ?? ""}|${x.city}`
              )
            : [],
      },
      deliveryMode,
      status: "draft",
      reviewStatus: "pending",
      startDate: startDate ? toISODate(startDate) : todayISODate(),
      endDate: endDate ? toISODate(endDate) : undefined,
    });
};

const handleLaunch = async () => {
  if (!mediaItems.length) {
    alert(t("ad.overview.alerts.noMediaAttached"));
    return;
  }

  const cleanTargetUrl = targetUrl.trim();

  if (!/^https?:\/\//i.test(cleanTargetUrl)) {
    alert(t("ad.overview.alerts.invalidUrl"));
    return;
  }

  if (isLaunching) return;

  try {
    setIsLaunching(true);

    await launchAd();

    navigate("/advertisement/post-upload", {
      replace: true,
    });
  } catch (err) {
    console.error("[AdOverview] ad upload failed:", err);

    alert(
      t(
        "ad.overview.alerts.uploadFailed",
        "The advertisement could not be submitted. Please try again."
      )
    );
  } finally {
    setIsLaunching(false);
  }
};


  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  //Changing from country to another class
  React.useEffect(() => {
    if (targetingMode !== "countries") {
      setSelectedCountries([]); // clear selection when leaving By Country
    }
  }, [targetingMode]);

  React.useEffect(() => {
    if (targetingMode !== "cities") {
      setSelectedCityCountryISO("");
      setSelectedCityState("");
      setSelectedCities([]);
    }
  }, [targetingMode]);
  
 //Start Date not after end date
 useEffect(() => {
  if (!startDate || !endDate) return;

  if (startOfDay(endDate).getTime() < startOfDay(startDate).getTime()) {
    setEndDate(startDate); // keeps it valid (end >= start)
    // or: setEndDate(null); if you prefer clearing instead
  }
}, [startDate, endDate]);


  const handleBack = () => {
    const backTo = state?.fromPath || '/advertisement/new';
  
    navigate(backTo, {
      state: {
        draft: {
          mediaItems,            // { existingMedia, mediaItems }
          targetUrl,
          buttonLabel,
          campaignName,          // 👈 this is the “Name” you edit on overview
        },
      },
    });
  };
  

  const trimmedUrl = (targetUrl || '').trim();
  const hasValidUrl = /^https?:\/\//i.test(trimmedUrl);
  
// ---------- UI ----------
return (
  <div style={pageShellStyle}>
    {/* Header */}
    <header style={pageHeaderStyle}>
      <div style={pageHeaderLeft} onClick={() => navigate('/advertisementstart')}>
        <ChoshiiLogo
          animateSheen
          fontFamily="'Playfair Display', serif"
          style={{ width: 160 }}
          padding={0}
        />
      </div>
    </header>

    <main style={pageMainStyle}>
      <div style={layoutGridStyle}>
        {/* LEFT: controls */}
        <section style={leftPanelStyle}>

          {/* Campaign details */}
          <div style={cardSectionStyle}>
            <h2 style={subHeadingStyle}>{t("ad.overview.campaignDetails.title")}</h2>
            <div style={labelStyle}>
            {t("ad.overview.campaignDetails.nameLabel")}
              <input
                type="text"
                maxLength={120}
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                style={inputStyle}
                placeholder={t("ad.overview.campaignDetails.namePlaceholder")} 
              />
            </div>

            <p style={helperTextStyle}>
              {t("ad.overview.campaignDetails.helper")}
            </p>
          </div>

          {/* Targeting mode */}
          <div style={cardSectionStyle}>
            <h2 style={subHeadingStyle}>{t("ad.overview.targeting.title")}</h2>

            <div style={chipRowStyle}>
              
              <button
                type="button"
                className="ad-chip"
                onClick={() => setTargetingMode('continent')}
                style={targetingMode === 'continent' ? chipActiveStyle : chipStyle}
              >
                {t("ad.overview.targeting.modes.continent")}

              </button>
              <button
                type="button"
                className="ad-chip"
                onClick={() => setTargetingMode('countries')}
                style={targetingMode === 'countries' ? chipActiveStyle : chipStyle}
              >
                {t("ad.overview.targeting.modes.countries")}

              </button>
              <button
  type="button"
  className="ad-chip"
  onClick={() => setTargetingMode("cities")}
  style={targetingMode === "cities" ? chipActiveStyle : chipStyle}
>
🗼By City
</button>

            </div>

            {targetingMode === 'continent' && (
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>
                {t("ad.overview.targeting.continentLabel")}
                  <select
                    value={selectedContinent}
                    onChange={(e) => {
                      setSelectedContinent(e.target.value);
                      setSelectedCountries([]);
                    }}
                    style={selectStyle}
                  >
                    {CONTINENTS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
                <p style={helperTextStyle}>
                {t("ad.overview.targeting.continentHelperPrefix")}{' '}
                  <strong>{selectedContinent}</strong>.
                </p>
              </div>
            )}

{targetingMode === "countries" && (
  <div style={{ marginTop: 0 }}>
    <CountriesDropdown
      label={t("ad.overview.targeting.countriesLabel")}
      options={ALL_COUNTRIES}
      value={selectedCountries}
      onToggle={toggleCountry}
      placeholder={t("ad.overview.targeting.noneSelected")}
      labelStyle={labelStyle}
      selectStyle={selectStyle}
    />
     <p style={helperTextStyle}>
                {t("ad.overview.targeting.selectedPrefix")}{' '}
                  {selectedCountries.length
                    ? selectedCountries
                    .map((code) => COUNTRY_CODE_TO_NAME.get(code) ?? code)
                    .join(", ")
                    : t("ad.overview.targeting.noneSelected")}
                </p>
  </div>
)}

{targetingMode === "cities" && (
  <CityChoice
    labelCountry="Country"
    labelState="State"
    labelCities="Cities"
    countryOptions={ALL_COUNTRIES}          // same list you already built
    selectedCountryISO={selectedCityCountryISO}
    onCountryChange={(iso) => {
      setSelectedCityCountryISO(iso);
      setSelectedCityState("");
    }}
    selectedState={selectedCityState}
    onStateChange={(st) => {
      setSelectedCityState(st);
    }}
    selectedCities={selectedCities}
    onToggleCity={toggleCity}
    labelStyle={labelStyle}
    selectStyle={selectStyle}
  />
)}

          </div>



          {/* Budget & delivery */}
          <div style={cardSectionStyle}>
            <h2 style={subHeadingStyle}>{t("ad.overview.budget.title")}</h2>

            <div style={labelStyle}>
  <div>{t("ad.overview.budget.dailyBudgetLabel")}</div>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={dailyBudget ? String(dailyBudget) : ""}
      placeholder="0"
      onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
      onKeyDown={(e) => {
        if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
      }}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d]/g, "");
        setDailyBudget(raw === "" ? 0 : Number(raw));
      }}
      style={inputStyle}
    />
    <span style={{ opacity: 0.8 }}>{t("ad.overview.budget.perDaySuffix")}</span>
  </div>
</div>



<div style={labelStyle}>
  <div>{t("ad.overview.budget.totalBudgetLabel", "Total spending limit (optional)")}</div>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={totalBudget === "" ? "" : String(totalBudget)}
      placeholder={t("ad.overview.budget.unlimitedPlaceholder", "Unlimited")}
      onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
      onKeyDown={(e) => {
        if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
      }}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d]/g, "");
        setTotalBudget(raw === "" ? "" : Number(raw));
      }}
      style={inputStyle}
    />
    <span style={{ opacity: 0.8 }}>€</span>
  </div>

  <p style={helperTextStyle}>
    {t(
      "ad.overview.budget.totalBudgetHelper",
      "If set, the campaign will stop once this total amount is spent."
    )}
  </p>
</div>

<div style={labelStyle}>
  {t("ad.overview.schedule.startDate", "Start date")}
  <input
  ref={startInputRef}
  readOnly
  value={formatISO(startDate)}
  placeholder="yyyy-mm-dd"
  style={dateInputStyle}
  onClick={() => {
    setActiveField("start");
    setCalendarOpen(true);
  }}
/>
</div>

<div style={labelStyle}>
  {t("ad.overview.schedule.endDate", "End date")}
  <input
  ref={endInputRef}
  readOnly
  value={formatISO(endDate)}
  placeholder={t("ad.overview.schedule.endDatePlaceholder", "Select end date")}
  style={dateInputStyle}
  onClick={() => {
    setActiveField("end");
    setCalendarOpen(true);
  }}
/>

</div>

<CalendarModal
  open={calendarOpen}
  onOpenChange={setCalendarOpen}
  title={activeField === "start" ? "Start date" : "End date"}
  value={activeField === "start" ? startDate : endDate}
  anchorRef={activeAnchorRef as unknown as React.RefObject<HTMLElement | null>}
  minDate={activeField === "start" ? new Date() : startDate ?? new Date()}
  onSelect={(date) => {
    if (activeField === "start") {
      setStartDate(date);
      if (endDate && endDate < date) setEndDate(null);
    } else {
      setEndDate(date);
    }
  }}
/>


            <div style={{ marginTop: 12 }}>
              <div style={labelTextOnlyStyle}>{t("ad.overview.budget.deliveryQuestion")}</div>
              <div style={chipRowStyle}>
                <button
                  type="button"
                  className="ad-chip"
                  onClick={() => setDeliveryMode('light')}
                  style={deliveryMode === 'light' ? chipActiveStyle : chipStyle}
                >
                  {t("ad.overview.budget.deliveryModes.light")}
                </button>
                <button
                  type="button"
                  className="ad-chip"
                  onClick={() => setDeliveryMode('balanced')}
                  style={deliveryMode === 'balanced' ? chipActiveStyle : chipStyle}
                >
                  {t("ad.overview.budget.deliveryModes.balanced")}
                </button>
                <button
                  type="button"
                  className="ad-chip"
                  onClick={() => setDeliveryMode('frequent')}
                  style={deliveryMode === 'frequent' ? chipActiveStyle : chipStyle}
                >
                 {t("ad.overview.budget.deliveryModes.frequent")}
                </button>
              </div>
            </div>

            <div style={estimateBoxStyle}>
            {t("ad.overview.budget.estimatePrefix")} <strong>{dailyBudget || 0} {t("ad.overview.budget.perDaySuffix")}</strong> {t("ad.overview.budget.estimateMiddle")}{' '}
              <strong>
                {impressionEstimate.min.toLocaleString()} –{' '}
                {impressionEstimate.max.toLocaleString()}
              </strong>{' '}
              {t("ad.overview.budget.estimateSuffix")}
            </div>
          </div>

          {/* Actions */}
          <div style={actionsRowStyle}>
            <button type="button" onClick={handleBack} style={secondaryButtonStyle}>
            {t("ad.overview.actions.back")}
            </button>

            <button
  type="button"
  onClick={handleLaunch}
  disabled={isLaunching}
  style={{
    ...primaryButtonStyle,
    opacity: isLaunching ? 0.6 : 1,
    cursor: isLaunching ? "not-allowed" : "pointer",
  }}
>
  {isLaunching
    ? t("ad.overview.actions.uploading", "Uploading...")
    : t("ad.overview.actions.launch")}
</button>
          </div>
        </section>

        {/* RIGHT: ad preview */}
        <section style={rightPanelStyle}>
          <h2 style={subHeadingStyle}></h2>

          {/* Summary box in its own section under the post */}
          <div
            style={{
              marginBottom: 16,
              marginTop: -20,
              maxWidth: 640,
              marginInline: 'auto',
            }}
          >
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              {t("ad.overview.summary.title")}
            </h3>

            <div style={summaryBoxStyle}>
            <div style={summaryRowStyle}>
              <span>{t("ad.overview.summary.rows.campaignName", "Campaign/ project name")}</span>
              <span>{(campaignName || "").trim() || t("ad.overview.summary.untitled", "Untitled")}</span>
            </div>
              <div style={summaryRowStyle}>
                <span>{t("ad.overview.summary.rows.location")}</span>
                <span>
                  {targetingMode === 'continent' && selectedContinent}
                  {targetingMode === 'countries' &&
                    (selectedCountries.length
                      ? selectedCountries
                      .map((code) => COUNTRY_CODE_TO_NAME.get(code) ?? code)
                      .join(", ")
                      : t("ad.overview.summary.noCountrySelected"))}

                      {/* city targeting instead of worldwide */}
                      {targetingMode === "cities" &&
  (selectedCities.length ? (
    <>
      {Array.from(
        selectedCities.reduce((acc, x) => {
          const countryName = COUNTRY_CODE_TO_NAME.get(x.countryISO) ?? x.countryISO;
          const statePart = x.countryISO === "US" && x.state ? ` — ${x.state}` : "";
          const key = `${countryName}${statePart}`;
          const list = acc.get(key) ?? [];
          list.push(x.city);
          acc.set(key, list);
          return acc;
        }, new Map<string, string[]>())
      )
        .map(([k, cities]) => `${k}: ${cities.join(", ")}`)
        .join(" | ")}
    </>
  ) : (
    t("ad.overview.summary.noCitySelected", "No city selected")
  ))}

                </span>
              </div>
              <div style={summaryRowStyle}>
                <span>{t("ad.overview.summary.rows.dailyBudget")}</span>
                <span>{dailyBudget || 0} €</span>
              </div>
              {totalBudget !== '' && (
                <div style={summaryRowStyle}>
                  <span>{t("ad.overview.summary.rows.totalBudget")}</span>
                  <span>{totalBudget} €</span>
                </div>
              )}
              {startDate && (
  <div style={summaryRowStyle}>
    <span>{t('ad.overview.summary.rows.startDate', 'Start date')}</span>
    <span>{toISODate(startDate)}</span>
  </div>
)}

{endDate && (
  <div style={summaryRowStyle}>
    <span>{t('ad.overview.summary.rows.endDate')}</span>
    <span>{toISODate(endDate)}</span>
  </div>
)}

              <div style={summaryRowStyle}>
                <span>{t("ad.overview.summary.rows.delivery")}</span>
                <span>
                {deliveryMode === "light" && t("ad.overview.summary.deliveryValues.light")}
                {deliveryMode === "balanced" && t("ad.overview.summary.deliveryValues.balanced")}
                {deliveryMode === "frequent" && t("ad.overview.summary.deliveryValues.frequent")}

                </span>
              </div>
              <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                {t("ad.overview.summary.billingNote")}
              </div>
            </div>
          </div>
          
          {/* Main post card */}
          <div style={previewCardStyle}>
  {/* Header: profile + CTA */}
<div style={previewHeaderStyle}>
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={avatarStyle(user?.picture)}>
      {!user?.picture && '👻'}
    </div>
    <div>
      <div style={{ fontWeight: 600 }}>
        {user?.profilename || t('gallery.edit.anonymous')}
      </div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{t("ad.overview.preview.sponsored")}</div>
    </div>
  </div>

  {hasValidUrl ? (
    <a
      href={trimmedUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        ...ctaButtonStyle,
        textDecoration: 'none',
        display: 'inline-block',
      }}
    >
      {buttonLabel || t("ad.overview.preview.visitWebsiteFallback")}
</a>
  ) : (
    <button
      type="button"
      style={{
        ...ctaButtonStyle,
        opacity: 0.5,
        cursor: 'not-allowed',
      }}
      disabled
      title={t("ad.overview.preview.ctaDisabledTitle")}
    >
      {buttonLabel || t("ad.overview.preview.visitWebsiteFallback")}
    </button>
  )}
</div>


 {/* Media gallery – same style as AdUpload */}
<div style={previewScrollerStyle}>
  {mediaItems.length === 0 && (
    <div style={{ fontSize: 13, opacity: 0.7 }}>
      {t("ad.overview.preview.noMediaAttachedHint")}
    </div>
  )}

  {mediaItems.map((item, index) => {
    const url = srcFor(item);
    const overlay = overlayStyleFor(item.filterId ?? null);
    if (!url) return null;

    return (
      <div key={item.id} style={previewTileWrapperStyle}>
        {isVideo(item) ? (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            <video
              src={url}
              muted
              controls
              preload="metadata"
              style={{
                display: 'block',
                maxWidth: MEDIA_TILE_WIDTH,
                maxHeight: MEDIA_TILE_WIDTH,
                width: 'auto',
                height: 'auto',
                borderRadius: 12,
              }}
            />
            {overlay && (
              <div
                style={{
                  ...overlay,
                  borderRadius: 12,
                  position: 'absolute',
                  inset: 0,
                }}
              />
            )}
          </div>
        ) : (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            <img
              src={url}
              alt={`media-${index}`}
              loading="lazy"
              style={{
                display: 'block',
                maxWidth: MEDIA_TILE_WIDTH,
                maxHeight: MEDIA_TILE_WIDTH,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                objectPosition: 'center',
                borderRadius: 12,
              }}
            />
            {overlay && (
              <div
                style={{
                  ...overlay,
                  borderRadius: 12,
                  position: 'absolute',
                  inset: 0,
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  })}
</div>



          </div>


        </section>
      </div>
    </main>
  </div>
);

  
};

/* ====== styles ====== */

const pageShellStyle: React.CSSProperties = {
  minHeight: '100vh',
  width: '100%',
  background:
    'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
  color: '#f9fafb',
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const pageHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 24px',
  background: 'rgba(2, 6, 23, 0.86)',
  backdropFilter: 'blur(10px)',
};

const pageHeaderLeft: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};

const pageMainStyle: React.CSSProperties = {
  padding: '20px 24px 32px',
};

const layoutGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 360px) minmax(0, 1fr)',
  gap: 20,
  alignItems: 'flex-start',
};


const leftPanelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 15,          // was 16
};

const rightPanelStyle: React.CSSProperties = {
  minWidth: 0,
  marginTop: -17, 
};

const subHeadingStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  marginBottom: 13,
  marginTop: -2,
};

const cardSectionStyle: React.CSSProperties = {
  background:
    'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(15,23,42,0.99))',
  borderRadius: 20,
  border: '1px solid rgba(148,163,184,0.4)',
  padding: 12,       // was 16
  maxWidth: 350,
  boxShadow: '0 20px 40px rgba(0,0,0,0.7)',
};

const previewCardStyle: React.CSSProperties = {
  background:
    'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(15,23,42,0.99))',
  borderRadius: 24,
  border: '1px solid rgba(148,163,184,0.35)',
  padding: '20px 18px 40px',
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  color: '#e5e7eb',
  maxWidth: 1200,
  margin: '0 auto',
};


const previewScrollerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: `${MEDIA_GAP}px`,
  paddingBottom: 10,
  paddingLeft: CONTENT_X_OFFSET,
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  whiteSpace: 'nowrap',
  transform: `translateX(${MEDIA_LEFT_NUDGE}px)`,
  alignItems: 'center',
};

const previewTileWrapperStyle: React.CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  background: 'transparent',
  position: 'relative',
  boxSizing: 'border-box',
  margin: 0,
  padding: 0,
};


const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 13,
  marginTop: 8,
};

const labelTextOnlyStyle: React.CSSProperties = {
  fontSize: 13,
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  backgroundColor: '#020617',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.8)',
  padding: '8px 10px',
  color: '#e5e7eb',
  fontSize: 13,
  outline: 'none',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  borderRadius: 999,
};

const chipRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 6,
};

const chipStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.5)',
  backgroundColor: 'rgba(15,23,42,0.9)',
  color: '#e5e7eb',
  fontSize: 12,
  cursor: 'pointer',
};

const chipActiveStyle: React.CSSProperties = {
  ...chipStyle,
  background:
    'linear-gradient(135deg, #eab308, #facc15)',
  color: '#111827',
  border: '1px solid #facc15',
};

const helperTextStyle: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.8,
  marginTop: 5,
  marginBottom: -1,
};

const estimateBoxStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '8px 10px',
  borderRadius: 12,
  backgroundColor: 'rgba(15,23,42,0.85)',
  border: '1px dashed rgba(148,163,184,0.6)',
  fontSize: 13,
};

const actionsRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  marginTop: 10,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  background:
    'linear-gradient(135deg, #eab308, #facc15)',
  color: '#111827',
  fontSize: 14,
  fontWeight: 600,
  marginLeft: 'auto',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.7)',
  backgroundColor: 'rgba(15,23,42,0.95)',
  color: '#e5e7eb',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
};

const previewHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',  // 🔹 button on the right
  gap: 10,
  marginBottom: 12,
};


const avatarStyle = (picture?: string | null): React.CSSProperties => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  backgroundColor: picture ? 'transparent' : 'rgb(38, 29, 162)',
  backgroundImage: picture ? `url(${picture})` : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: picture ? 0 : 24,
});

const ctaButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  background: 'linear-gradient(135deg, #22c55e, #a3e635)',
  color: '#022c22',
  fontSize: 14,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};



const summaryBoxStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 10px 8px',
  borderRadius: 14,
  backgroundColor: '#020617',
  border: '1px solid rgba(148,163,184,0.5)',
  fontSize: 13,
};

const summaryRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 4,
};

export default AdOverview;
